if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.isSubmitting = false;
        // Delegate to the host so replacing the inner form does not lose submit handling.
        this.addEventListener('submit', (event) => {
          if (event.target.closest('product-form') === this) this.onSubmitHandler(event);
        });
        this.addEventListener('click', (event) => {
          const button = event.target.closest?.('[data-skip-cart-button="true"]');
          if (!button || button.closest('product-form') !== this) return;
          event.preventDefault();
          this.onSubmitHandler({ preventDefault() {}, submitter: button });
        });
      }

      connectedCallback() {
        this.refreshFormElements();
        // A custom element may connect before its children are parsed/inserted.
        queueMicrotask(() => { if (this.isConnected) this.refreshFormElements(); });
      }

      refreshFormElements() {
        this.form = this.querySelector('form');
        this.productInfo = this.closest('product-info');
        if (this.variantIdInput) this.variantIdInput.disabled = false;
        this.cart = this.resolveCartPresenter();
        this.submitButton = this.querySelector('[name="add"][type="submit"]') || this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector('.main-atc__label__text, .button__label');
        if (document.querySelector('cart-drawer')) this.submitButton?.setAttribute('aria-haspopup', 'dialog');
        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      handleSubmit(evt, options = {}) {
        return this.onSubmitHandler(evt, options);
      }

      async onSubmitHandler(evt, options = {}) {
        evt.preventDefault();
        this.refreshFormElements();
        const activeSubmitButton = options.submitButton || evt.submitter || this.submitButton;
        const traceId = window.CartDebug?.start(this, activeSubmitButton);
        const log = (stage, data = {}) => window.CartDebug?.log(stage, { traceId, ...data });
        if (!this.form || !this.variantIdInput || !activeSubmitButton) {
          log('submit.blocked', { reason: 'missing-form-or-button' });
          this.handleErrorMessage('Purchase controls are not ready. Please reload the product page.');
          return;
        }
        if (this.isSubmitting) {
          log('submit.blocked', { reason: 'already-submitting' });
          return;
        }
        if (activeSubmitButton.disabled || activeSubmitButton.getAttribute('aria-disabled') === 'true') {
          log('submit.blocked', { reason: 'button-disabled' });
          return;
        }

        if (!this.form.reportValidity()) return;

        // Product/cart upsells keep their selected variant on the upsell
        // element. They can be nested inside the main product-info element,
        // so validating them against the main product variant picker rejects
        // an otherwise valid add-to-cart request.
        const upsell = this.closest('product-info-upsell, cart-drawer-upsell');
        if (upsell?.dataset.id) this.variantIdInput.value = upsell.dataset.id;

        const variantPicker = upsell ? null : this.closest('product-info')?.querySelector('variant-selects');
        if (!upsell && this.closest('product-info')?.querySelector('subscription-widget')?.dataset.variantPending === 'true') {
          log('submit.blocked', { reason: 'subscription-pending' });
          this.handleErrorMessage('Please wait for the selected purchase options to finish updating.');
          return;
        }
        if (!upsell && !this.validateCustomProductFields()) {
          log('submit.blocked', { reason: 'custom-field-invalid' });
          return;
        }
        const selectedVariantId = variantPicker?.dataset.selectedVariantId;
        const resolvedVariantId = variantPicker?.selectedVariant?.id;
        if (!this.variantIdInput.value) {
          this.variantIdInput.value = resolvedVariantId || selectedVariantId || this.dataset.variantId || '';
        }
        const submittedVariantId = this.variantIdInput.value;
        const selectedOptionsMatch =
          !resolvedVariantId || String(resolvedVariantId) === String(submittedVariantId);
        if (
          !submittedVariantId ||
          (selectedVariantId && submittedVariantId !== selectedVariantId) ||
          !selectedOptionsMatch
        ) {
          log('submit.blocked', { reason: 'variant-mismatch', submittedVariantId, selectedVariantId, resolvedVariantId });
          const message = 'The selected product option could not be confirmed. Please select the option again.';
          this.handleErrorMessage(message);
          this.dispatchCartErrorEvent(message, 'INVALID_VARIANT');
          console.error(message, { submittedVariantId, selectedVariantId });
          return;
        }

        const skipCart = options.skipCart === true
          || activeSubmitButton.dataset.skipCartButton === 'true'
          || this.variantIdInput.dataset.skipCart === 'true';

        this.handleErrorMessage();
        this.error = false;
        this.isSubmitting = true;

        let loadingSpinner;
        let linesUpdateDeferred;
        let addConfirmed = false;
        let requestStarted = false;
        let stage = 'prepare';
        let slowTimer;
        try {
          activeSubmitButton.setAttribute('aria-disabled', 'true');
          activeSubmitButton.classList.add('loading');
          loadingSpinner = activeSubmitButton.querySelector('.loading-overlay__spinner, .loading__spinner');
          loadingSpinner?.classList.remove('hidden');
          log('prepare.start');

          stage = 'prepare.fetch-config';
          const config = fetchConfig('json');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];

          stage = 'prepare.form-data';
          const formData = new FormData(this.form);
          // Subscription widgets sit outside the product form and Shopify/app
          // scripts may recreate or clear their hidden selling_plan input during
          // product-info updates. Restore a missing field from the live widget,
          // but never overwrite or delete a plan supplied by the submitted form.
          // One-time selection is handled by the widget disabling its own input
          // before FormData is created; absence of a widget is not that action.
          if (!upsell) {
            const subscriptionWidget = this.closest('product-info')?.querySelector('subscription-widget');
            const subscriptionSelected = subscriptionWidget
              ?.querySelector('.subscription__box--subscription')
              ?.dataset.selected === 'true';
            const selectedPlan = subscriptionWidget?.querySelector(
              'select option:checked, input[data-selling-plan-id]:checked'
            );
            const sellingPlanId = selectedPlan?.dataset.sellingPlanId || selectedPlan?.value;
            if (!formData.has('selling_plan') && subscriptionWidget?.dataset?.planManaged !== 'external' && subscriptionSelected && sellingPlanId) {
              formData.set('selling_plan', sellingPlanId);
            }
          }
          stage = 'prepare.sections';
          if (this.cart) {
            const sections = typeof this.cart.getSectionsToRender === 'function'
              ? this.cart.getSectionsToRender()
              : [
                  { id: 'cart-drawer' },
                  { id: 'cart-icon-bubble' },
                ];
            formData.append(
              'sections',
              sections.map((section) => section.section || section.id).filter(Boolean).join(',')
            );
            formData.append('sections_url', window.location.pathname + window.location.search);
            this.cart.setActiveElement?.(document.activeElement);
          }
          config.body = formData;

          const variantId = formData.get('id');
          const quantity = parseInt(formData.get('quantity')) || 1;
          stage = 'prepare.upsells';
          if (!upsell) this.appendSelectedProductUpsells(formData);
          stage = 'prepare.events';
          linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

          log('prepare.ready', { variantId, quantity, skipCart, items: window.CartDebug?.summarizeForm(formData) });
          const sendRequest = async () => {
            stage = 'request';
            requestStarted = true;
            log('request.start');
            try {
              if (window.GeneralStoreRuntime?.requestJson) {
                return await window.GeneralStoreRuntime.requestJson(`${routes.cart_add_url}`, config, { traceId });
              }
              const controller = new AbortController();
              const timer = window.setTimeout(() => controller.abort(), 15000);
              try {
                const response = await fetch(`${routes.cart_add_url}`, { ...config, signal: controller.signal });
                log('request.response', { status: response.status });
                const data = await response.json();
                if (!data || typeof data !== 'object' || Array.isArray(data)) throw new Error('Invalid cart response');
                if (!response.ok) throw Object.assign(new Error('Cart request failed'), { responseData: data, responseStatus: response.status });
                return data;
              } finally {
                window.clearTimeout(timer);
              }
            } catch (error) {
              if (error.responseData && error.responseStatus >= 400 && error.responseStatus < 500) {
                return { ...error.responseData, status: error.responseStatus };
              }
              throw error;
            }
          };
          const drawer = document.querySelector('cart-drawer');
          stage = 'queue';
          log('queue.wait', { pending: Boolean(drawer?.cartMutationQueue) });
          slowTimer = window.setTimeout(() => {
            log('submit.slow', { phase: stage });
            this.handleErrorMessage('Your cart update is still pending. Please wait or check your cart before trying again.');
            this.showCartRecoveryLink();
          }, 10000);
          const response = await (drawer?.enqueueCartMutation ? drawer.enqueueCartMutation(sendRequest) : sendRequest());
          window.clearTimeout(slowTimer);
          this.handleErrorMessage();
          if (response.status) {
            log('request.rejected', { status: response.status });
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: variantId,
              errors: response.errors || response.description,
              message: response.message,
            });
            this.handleErrorMessage(response.description || response.message || window.cartStrings?.error || 'Unable to add this item.');
            this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
            linesUpdateDeferred?.reject(new Error(response.description || response.message));

            // Rate limits, quantity limits, and plan validation are not proof
            // that the variant is sold out. Keep the action usable after the error.
            return;
          }

          if (!response.id && !response.items?.some((item) => item.id)) {
            throw new Error('Cart add response is missing line items');
          }
          addConfirmed = true;
          stage = 'render';
          log('request.confirmed', { itemCount: response.items?.length || 1, sections: Object.keys(response.sections || {}) });

          // As in Plum: render this add before reconciling drawer add-ons.
          // An add response is not a complete cart; never reuse the pre-add cache.
          const preparedDrawer = !upsell && drawer ? drawer : null;
          if (preparedDrawer) {
            if (typeof preparedDrawer.renderContents !== 'function'
              || typeof preparedDrawer.prepareCheckout !== 'function') {
              throw new Error('Cart add-ons are not ready. Please review your cart.');
            }
            preparedDrawer.renderContents(response, { open: false, reconcile: false });
            preparedDrawer.cartStateStale = true;
            const result = await preparedDrawer.prepareCheckout();
            if (result?.error) throw result.error;
            if (preparedDrawer.querySelector('cart-drawer-upsell[data-toggle="true"][data-id]')
              && !Array.isArray(result?.cart?.items)) {
              throw new Error('Cart add-ons could not be verified.');
            }
          }

          if (skipCart) {
            log('checkout.prepare');
            this.resolveCartLinesUpdate(linesUpdateDeferred);
            // Use the same guest-checkout POST as Plum, after add-ons are verified.
            const checkoutForm = document.createElement('form');
            checkoutForm.method = 'post';
            checkoutForm.action = window.routes.cart_url;
            checkoutForm.hidden = true;
            const checkoutInput = document.createElement('input');
            checkoutInput.type = 'hidden';
            checkoutInput.name = 'checkout';
            checkoutInput.value = 'Checkout';
            checkoutForm.append(checkoutInput);
            document.body.append(checkoutForm);
            HTMLFormElement.prototype.submit.call(checkoutForm);
            return;
          }

          if (!this.cart) {
            log('redirect.cart', { reason: 'no-presenter' });
            this.resolveCartLinesUpdate(linesUpdateDeferred);
            window.location = window.routes.cart_url;
            return;
          }

          this.resolveCartLinesUpdate(linesUpdateDeferred);

          this.error = false;
          const quickAddModal = this.closest('quick-add-modal');
          if (preparedDrawer) {
            quickAddModal?.hide(true);
            preparedDrawer.open();
          } else if (quickAddModal) {
            quickAddModal.hide(true);
            // Keep rendering inside this promise: an exception in a detached
            // modalClosed/timer callback must not escape the recovery path.
            this.measureCartRender(() => this.renderCartContents(response));
          } else {
            // Rendering the purchase result is the critical path. Open the
            // drawer before notifying optional subscribers such as quantity
            // rules, recommendations, analytics, or app integrations.
            this.measureCartRender(() => this.renderCartContents(response));
          }

          log('render.complete', { presenter: this.cart?.tagName, active: this.cart?.classList.contains('active') });
          const startMarker = window.CartPerformance?.createStartingMarker('add:wait-for-subscribers');
          if (!this.error)
            Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: variantId,
              cartData: response,
            })).then(() => {
              if (startMarker) window.CartPerformance?.measureFromMarker('add:wait-for-subscribers', startMarker);
            }).catch((error) => console.error('Cart update subscribers failed', error));
        } catch (e) {
          log('submit.error', { phase: stage, addConfirmed, requestStarted, name: e.name, code: e.code || e.responseStatus || 'UNKNOWN' });
          console.error(e);
          if (addConfirmed) {
            // The cart has already changed. Go to its server-rendered page;
            // do not tell the shopper to add the same item again.
            log('redirect.cart', { reason: 'render-or-post-add-error' });
            window.location.assign(window.routes.cart_url);
            return;
          }
          const message = !requestStarted
            ? 'The cart request could not be prepared. Please reload the product page and try again.'
            : e.userMessage || 'We could not confirm the cart update. Please check your cart before trying again.';
          this.handleErrorMessage(message);
          if (!e.responseData) this.showCartRecoveryLink();
          this.dispatchCartErrorEvent(message, 'SERVICE_UNAVAILABLE');
          linesUpdateDeferred?.reject(e);
        } finally {
          window.clearTimeout(slowTimer);
          this.isSubmitting = false;
          activeSubmitButton.classList.remove('loading');
          if (!this.error) activeSubmitButton.removeAttribute('aria-disabled');
          loadingSpinner?.classList.add('hidden');

          log('submit.finish', { addConfirmed, requestStarted, locked: activeSubmitButton.getAttribute('aria-disabled') === 'true' });
          window.CartPerformance?.measureFromEvent('add:user-action', evt);
        }
      }

      appendSelectedProductUpsells(formData) {
        // Do not submit native-wallet metadata to the AJAX cart endpoint.
        Array.from(formData.keys()).forEach((key) => { if (key.startsWith('shopify-additional-items[')) formData.delete(key); });
        const selected = this.getSelectedAdditionalItems();
        if (!selected.length && !Array.from(formData.keys()).some(key => key.startsWith('items['))) return;

        // Bundle arrays already carry their main lines; convert to explicit indexes
        // before appending optional items so properties stay on the right line.
        let index = -1;
        const entries = Array.from(formData.entries());
        entries.forEach(([key]) => { if (key.startsWith('items[]')) formData.delete(key); });
        entries.forEach(([key, value]) => {
          if (!key.startsWith('items[]')) return;
          if (key === 'items[][id]') index += 1;
          if (index >= 0) formData.append(key.replace('items[]', `items[${index}]`), value);
        });
        const indexes = Array.from(formData.keys()).map(key => key.match(/^items\[(\d+)\]/)?.[1]).filter(x => x != null).map(Number);
        if (!indexes.length) {
          for (const [key, value] of Array.from(formData.entries())) {
            if (['id', 'quantity', 'selling_plan'].includes(key) || key.startsWith('properties[')) {
              const itemKey = key.startsWith('properties[') ? 'items[0][properties]' + key.slice(10) : `items[0][${key}]`;
              formData.append(itemKey, value); formData.delete(key);
            }
          }
          index = 0;
        } else {
          index = Math.max(...indexes);
          // Top-level ID/quantity would otherwise add an unintended extra main line.
          for (const key of ['id', 'quantity']) formData.delete(key);
          const plan = formData.get('selling_plan');
          for (const i of new Set(indexes)) {
            if (formData.has(`items[${i}][parent_id]`)) continue;
            if (plan && !formData.has(`items[${i}][selling_plan]`)) formData.append(`items[${i}][selling_plan]`, plan);
            for (const [key, value] of Array.from(formData.entries())) {
              if (key.startsWith('properties[') && !formData.has(`items[${i}][properties]` + key.slice(10))) formData.append(`items[${i}][properties]` + key.slice(10), value);
            }
          }
          formData.delete('selling_plan');
          Array.from(formData.keys()).forEach(key => { if (key.startsWith('properties[')) formData.delete(key); });
        }
        const parentId = formData.get('items[0][id]');
        selected.forEach((item) => {
          const prefix = `items[${++index}]`;
          formData.append(prefix + '[id]', item.id);
          formData.append(prefix + '[quantity]', item.quantity || '1');
          if (parentId) formData.append(prefix + '[parent_id]', parentId);
          for (const [key, value] of Object.entries(item.properties || {})) formData.append(prefix + '[properties][' + key + ']', value);
          if (item.sellingPlan) formData.append(prefix + '[selling_plan]', item.sellingPlan);
        });
      }

      getSelectedAdditionalItems() {
        if (!this.productInfo) return [];
        const items = [];
        this.productInfo.querySelectorAll('product-info-upsell[data-toggle="true"][data-selected="true"][data-id]').forEach((upsell) => {
          if (upsell.dataset.variantAvailable === 'false') return;
          items.push({
            id: upsell.dataset.id,
            quantity: '1',
            key: upsell.dataset.upsellKey || `${upsell.dataset.productId || ''}-${upsell.dataset.id}`,
            properties: {
              _theme_upsell_key: upsell.dataset.upsellKey || `${upsell.dataset.productId || ''}-${upsell.dataset.id}`,
            },
            sellingPlan: upsell.querySelector('input[name="selling_plan"]:not(:disabled)')?.value || '',
          });
        });
        this.productInfo
          .querySelectorAll('quantity-break-upsells:not([hidden]) [data-additional-key][data-product][data-selected="true"]')
          .forEach((item) => {
            items.push({
              id: item.dataset.product,
              quantity: '1',
              key: item.dataset.additionalKey,
              properties: {
                _theme_additional_key: item.dataset.additionalKey,
                _theme_parent_product_id: this.productInfo.dataset.productId || '',
                _theme_min_parent_quantity: item.dataset.minQuantity || '1',
                _theme_additional_kind: item.dataset.type || 'upsell',
              },
              sellingPlan: item.querySelector('input[name="selling_plan"]:not(:disabled)')?.value || '',
            });
          });
        this.productInfo
          .querySelectorAll('quantity-gifts .quantity-gift[data-additional-key][data-product][data-unlocked="true"]')
          .forEach((gift) => {
            items.push({
              id: gift.dataset.product,
              quantity: '1',
              key: gift.dataset.additionalKey,
              properties: {
                _theme_additional_key: gift.dataset.additionalKey,
                _theme_parent_product_id: this.productInfo.dataset.productId || '',
                _theme_min_parent_quantity: gift.dataset.quantity || '1',
                _theme_additional_kind: 'gift',
              },
              sellingPlan: gift.querySelector('input[name="selling_plan"]:not(:disabled)')?.value || '',
            });
          });
        const seen = new Set();
        return items.filter((item) => {
          const identity = `${Object.keys(item.properties || {})[0] || ''}:${item.key}`;
          if (!item.id || !item.key || seen.has(identity)) return false;
          seen.add(identity);
          return true;
        });
      }

      validateCustomProductFields() {
        if (!this.productInfo) return true;
        const fields = Array.from(this.productInfo.querySelectorAll('custom-product-field'));
        const firstInvalid = fields.find((field) => field.validate?.(false) === false);
        if (!firstInvalid) return true;
        firstInvalid.validate?.(true);
        return false;
      }

      getButtonLabel(button) {
        return button?.querySelector('.main-atc__label__text, .button__label');
      }

      resolveCartPresenter() {
        return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      }

      renderCartContents(response) {
        const presenter = this.resolveCartPresenter();
        this.cart = presenter;
        if (!presenter) {
          window.location.assign(window.routes.cart_url);
          return;
        }

        if (presenter.tagName === 'CART-DRAWER') {
          const html = response.sections?.['cart-drawer'];
          if (!html || !new DOMParser().parseFromString(html, 'text/html').querySelector('#CartDrawer')) {
            throw new Error('Cart drawer section missing from successful add response');
          }
        }
        if (typeof presenter.renderContents === 'function') {
          presenter.renderContents(response);
          return;
        }

        // Resilient drawer fallback for Shopify editor section reloads or a
        // custom-element registration failure. It mirrors CartDrawer without
        // requiring the element to have been upgraded first.
        if (presenter.tagName === 'CART-DRAWER') {
          const parser = new DOMParser();
          const drawerHtml = response.sections?.['cart-drawer'];
          const bubbleHtml = response.sections?.['cart-icon-bubble'];
          const source = drawerHtml && parser.parseFromString(drawerHtml, 'text/html').querySelector('#CartDrawer');
          const target = presenter.querySelector('#CartDrawer');
          const bubbleSource = bubbleHtml && parser.parseFromString(bubbleHtml, 'text/html').querySelector('#cart-icon-bubble');
          const bubbleTarget = document.querySelector('#cart-icon-bubble');
          if (!source || !target || !bubbleSource || !bubbleTarget) throw new Error('Cart sections could not be rendered');
          target.innerHTML = source.innerHTML;
          bubbleTarget.replaceWith(bubbleSource);
          presenter.classList.remove('is-empty');
          presenter.classList.add('animate', 'active');
          document.body.classList.add('overflow-hidden');
          presenter.querySelector('.drawer__inner')?.focus();
          return;
        }

        window.location.assign(window.routes.cart_url);
      }

      measureCartRender(callback) {
        if (window.CartPerformance?.measure) {
          window.CartPerformance.measure('add:paint-updated-sections', callback);
        } else {
          callback();
        }
      }

      handleErrorMessage(errorMessage = false) {
        this.errorMessageWrapper =
          this.querySelector('.product-form__error-message-wrapper');
        // Product-card quick add has no error container in some layouts.
        // Errors must remain visible even there; hideErrors cannot swallow a
        // failed/uncertain transaction.
        if (!this.errorMessageWrapper && errorMessage) {
          this.errorMessageWrapper = document.createElement('div');
          this.errorMessageWrapper.className = 'product-form__error-message-wrapper';
          this.errorMessageWrapper.setAttribute('role', 'alert');
          const label = document.createElement('span');
          label.className = 'product-form__error-message';
          this.errorMessageWrapper.append(label);
          this.append(this.errorMessageWrapper);
        }
        if (!this.errorMessageWrapper) return;
        this.errorMessageWrapper.querySelector('[data-cart-recovery]')?.remove();
        this.errorMessage = this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      showCartRecoveryLink() {
        if (!this.errorMessageWrapper) return;
        const link = document.createElement('a');
        link.href = window.routes.cart_url;
        link.dataset.cartRecovery = 'true';
        link.textContent = ' View cart';
        this.errorMessageWrapper.append(link);
      }

      toggleSubmitButton(disable = true, text) {
        this.refreshFormElements();
        if (!this.submitButton) return;
        window.CartDebug?.log('button.state', { section: this.dataset.section, disable, submitting: this.isSubmitting });
        const purchaseButtons = [
          this.submitButton,
          ...this.querySelectorAll('[data-skip-cart-button="true"]'),
        ];
        if (disable) {
          purchaseButtons.forEach((button) => button.setAttribute('disabled', 'disabled'));
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          purchaseButtons.forEach((button) => {
            button.removeAttribute('disabled');
            button.removeAttribute('aria-disabled');
          });
          if (this.submitButtonText) {
            this.submitButtonText.textContent = this.submitButton.dataset.customLabel || window.variantStrings.addToCart;
          }
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form?.querySelector('[name=id]');
      }
    }
  );
}
